// src/components/UserUsageTracker/UserUsageTracker.jsx
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getUserUsage} from '../../../features/userUsageTracker/userUsageTrackerActions';
import {
    selectUsageData,
    selectUsageTrackerError,
    selectUsageTrackerLoading
} from '../../../features/userUsageTracker/userUsageTrackerSlice';
import './UserUsageTracker.css';

const UserUsageTracker = () => {
    const dispatch = useDispatch();
    const usageData = useSelector(selectUsageData);
    const loading = useSelector(selectUsageTrackerLoading);
    const error = useSelector(selectUsageTrackerError);

    // Filter states
    const [emailFilter, setEmailFilter] = useState('');
    const [activityFilter, setActivityFilter] = useState('');
    const [ipFilter, setIpFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [sortBy, setSortBy] = useState('totalClicks');
    const [sortOrder, setSortOrder] = useState('desc');

    useEffect(() => {
        dispatch(getUserUsage());
    }, [dispatch]);

    // Group and process data with smart email assignment
    const processedData = useMemo(() => {
        if (!usageData || usageData.length === 0) return [];

        // First, create a mapping of IP addresses to emails (non-null)
        const ipToEmailMap = {};
        usageData.forEach(item => {
            if (item.email && item.email !== 'null' && item.ipAddress) {
                ipToEmailMap[item.ipAddress] = item.email;
            }
        });

        // Group by IP address and activity combination
        const grouped = {};
        usageData.forEach(item => {
            const key = `${item.ipAddress}_${item.activity}`;
            const email = ipToEmailMap[item.ipAddress] || null;

            if (!grouped[key]) {
                grouped[key] = {
                    ipAddress: item.ipAddress,
                    email: email,
                    activity: item.activity,
                    totalClicks: 0,
                    dates: new Set(),
                    firstSeen: item.usageDate,
                    lastSeen: item.usageDate
                };
            }

            grouped[key].totalClicks += item.numberOfClicks || 0;
            grouped[key].dates.add(item.usageDate);

            // Update date ranges
            if (item.usageDate < grouped[key].firstSeen) {
                grouped[key].firstSeen = item.usageDate;
            }
            if (item.usageDate > grouped[key].lastSeen) {
                grouped[key].lastSeen = item.usageDate;
            }
        });

        return Object.values(grouped).map(item => ({
            ...item,
            uniqueDays: item.dates.size,
            displayEmail: item.email || 'Anonymous'
        }));
    }, [usageData]);

    // Apply filters
    const filteredData = useMemo(() => {
        return processedData.filter(item => {
            const emailMatch = !emailFilter ||
                item.displayEmail.toLowerCase().includes(emailFilter.toLowerCase());
            const activityMatch = !activityFilter ||
                item.activity.toLowerCase().includes(activityFilter.toLowerCase());
            const ipMatch = !ipFilter ||
                item.ipAddress.includes(ipFilter);
            const dateMatch = !dateFilter ||
                item.lastSeen >= dateFilter;

            return emailMatch && activityMatch && ipMatch && dateMatch;
        });
    }, [processedData, emailFilter, activityFilter, ipFilter, dateFilter]);

    // Apply sorting
    const sortedData = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
    }, [filteredData, sortBy, sortOrder]);

    // Get unique activities for filter dropdown
    const uniqueActivities = useMemo(() => {
        return [...new Set(processedData.map(item => item.activity))].sort();
    }, [processedData]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return '↕️';
        return sortOrder === 'asc' ? '↑' : '↓';
    };

    const clearFilters = () => {
        setEmailFilter('');
        setActivityFilter('');
        setIpFilter('');
        setDateFilter('');
    };

    if (loading) {
        return (
            <div className="usage-tracker-container">
                <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                    <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="usage-tracker-container">
                <div className="alert alert-danger usage-tracker__alert" role="alert">
                    <h4 className="alert-heading">Error Loading Usage Data</h4>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="usage-tracker-container">
            <div className="usage-tracker__header mb-4">
                <h2 className="usage-tracker__title">User Usage Analytics</h2>
                <p className="usage-tracker__subtitle text-muted">
                    Monitor user activity and engagement across the platform
                </p>
            </div>

            {/* Filters Section */}
            <div className="usage-tracker__filters card mb-4">
                <div className="card-header usage-tracker__filters-header">
                    <h5 className="mb-0">Filters & Search</h5>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label usage-tracker__label">Email</label>
                            <input
                                type="text"
                                className="form-control usage-tracker__input"
                                placeholder="Search by email..."
                                value={emailFilter}
                                onChange={(e) => setEmailFilter(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label usage-tracker__label">Activity</label>
                            <select
                                className="form-select usage-tracker__select"
                                value={activityFilter}
                                onChange={(e) => setActivityFilter(e.target.value)}
                            >
                                <option value="">All Activities</option>
                                {uniqueActivities.map(activity => (
                                    <option key={activity} value={activity}>{activity}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label usage-tracker__label">IP Address</label>
                            <input
                                type="text"
                                className="form-control usage-tracker__input"
                                placeholder="Search by IP..."
                                value={ipFilter}
                                onChange={(e) => setIpFilter(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label usage-tracker__label">Since Date</label>
                            <input
                                type="date"
                                className="form-control usage-tracker__input"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-12">
                            <button
                                className="btn btn-outline-secondary usage-tracker__clear-btn"
                                onClick={clearFilters}
                            >
                                Clear All Filters
                            </button>
                            <span className="usage-tracker__results-count ms-3 text-muted">
                                Showing {sortedData.length} of {processedData.length} results
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="card usage-tracker__summary-card">
                        <div className="card-body text-center">
                            <h3 className="usage-tracker__summary-number">{processedData.length}</h3>
                            <p className="usage-tracker__summary-label">Total Activities</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card usage-tracker__summary-card">
                        <div className="card-body text-center">
                            <h3 className="usage-tracker__summary-number">
                                {new Set(processedData.map(item => item.ipAddress)).size}
                            </h3>
                            <p className="usage-tracker__summary-label">Unique IPs</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card usage-tracker__summary-card">
                        <div className="card-body text-center">
                            <h3 className="usage-tracker__summary-number">
                                {processedData.reduce((sum, item) => sum + item.totalClicks, 0)}
                            </h3>
                            <p className="usage-tracker__summary-label">Total Clicks</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card usage-tracker__summary-card">
                        <div className="card-body text-center">
                            <h3 className="usage-tracker__summary-number">
                                {new Set(processedData.filter(item => item.email).map(item => item.email)).size}
                            </h3>
                            <p className="usage-tracker__summary-label">Registered Users</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="usage-tracker__table-container card">
                <div className="card-header usage-tracker__table-header">
                    <h5 className="mb-0">Usage Details</h5>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover usage-tracker__table mb-0">
                            <thead className="usage-tracker__table-head">
                            <tr>
                                <th
                                    className="usage-tracker__sortable-header"
                                    onClick={() => handleSort('displayEmail')}
                                >
                                    Email {getSortIcon('displayEmail')}
                                </th>
                                <th
                                    className="usage-tracker__sortable-header"
                                    onClick={() => handleSort('ipAddress')}
                                >
                                    IP Address {getSortIcon('ipAddress')}
                                </th>
                                <th
                                    className="usage-tracker__sortable-header"
                                    onClick={() => handleSort('activity')}
                                >
                                    Activity {getSortIcon('activity')}
                                </th>
                                <th
                                    className="usage-tracker__sortable-header"
                                    onClick={() => handleSort('totalClicks')}
                                >
                                    Total Clicks {getSortIcon('totalClicks')}
                                </th>
                                <th
                                    className="usage-tracker__sortable-header"
                                    onClick={() => handleSort('uniqueDays')}
                                >
                                    Active Days {getSortIcon('uniqueDays')}
                                </th>
                                <th
                                    className="usage-tracker__sortable-header"
                                    onClick={() => handleSort('firstSeen')}
                                >
                                    First Seen {getSortIcon('firstSeen')}
                                </th>
                                <th
                                    className="usage-tracker__sortable-header"
                                    onClick={() => handleSort('lastSeen')}
                                >
                                    Last Seen {getSortIcon('lastSeen')}
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center usage-tracker__no-data">
                                        No usage data found matching your filters.
                                    </td>
                                </tr>
                            ) : (
                                sortedData.map((item, index) => (
                                    <tr key={`${item.ipAddress}_${item.activity}_${index}`}
                                        className="usage-tracker__table-row">
                                        <td className="usage-tracker__email-cell">
                                                <span
                                                    className={`usage-tracker__email-badge ${!item.email ? 'usage-tracker__email-badge--anonymous' : 'usage-tracker__email-badge--registered'}`}>
                                                    {item.displayEmail}
                                                </span>
                                        </td>
                                        <td className="usage-tracker__ip-cell">
                                            <code className="usage-tracker__ip-code">{item.ipAddress}</code>
                                        </td>
                                        <td className="usage-tracker__activity-cell">{item.activity}</td>
                                        <td className="usage-tracker__clicks-cell">
                                                <span className="badge usage-tracker__clicks-badge">
                                                    {item.totalClicks}
                                                </span>
                                        </td>
                                        <td className="usage-tracker__days-cell">{item.uniqueDays}</td>
                                        <td className="usage-tracker__date-cell">{item.firstSeen}</td>
                                        <td className="usage-tracker__date-cell">{item.lastSeen}</td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserUsageTracker;